import React, { useEffect, useMemo } from 'react';
import BaseWrapper from 'components/layout/BaseWrapper';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import * as routes from 'constants/routes';
import { useWalletAddressTimer } from 'hooks/useWalletAddressTimer';
import {
  WalletAddressBanner,
  WalletAddressForm,
  ProgressBar,
  TimerDisplay,
  NetworkChip,
  FlowTag,
} from 'components/WalletEntry';
import { Belt } from 'components/WalletEntry/styled';
import { useOptionalTournament } from 'context/TournamentContext';
import { assignWalletToTournament, getWalletAssignmentForTournament } from 'api/walletService';
import { cancelTournament } from 'api/tournamentService';

import {
  GradientSection,
  Content,
  HeaderStack,
} from './styled';

const WalletAddressEntry: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { hash?: string; from?: string; tournamentId?: string } | null;
  const hash = locationState?.hash;
  const isJoinFlow = locationState?.from === 'tournament-join';
  const { timeLeft, isExpired, formatTime } = useWalletAddressTimer(600);
  const [loading, setLoading] = React.useState(false);

  // Chain comes from the tournament's stake_currency relation (populated by
  // backend on /api/tournaments/:id). Falls back to polygon when the tournament
  // hasn't loaded yet or is a legacy POL tournament without the relation.
  // useOptionalTournament so unit tests that don't provide a TournamentProvider
  // still mount the screen (defaults to polygon copy).
  const tournamentCtx = useOptionalTournament?.();
  const chain = useMemo<'polygon' | 'solana'>(() => {
    const raw: any = tournamentCtx?.tournamentDetails?.data;
    const attrs = raw?.attributes ?? raw?.data ?? raw;
    return attrs?.stake_currency?.chain === 'solana' ? 'solana' : 'polygon';
  }, [tournamentCtx]);

  // Build the "go back" path depending on flow type
  const goBackPath = isJoinFlow
    ? routes.TOURNAMENTS.replace(':id', id || '') + (hash ? `?hash=${hash}` : '')
    : routes.CREATING_TOURNAMENT.replace(':id', id || '');

  // Handle timer expiration
  useEffect(() => {
    if (!isExpired || !id) return;

    if (isJoinFlow) {
      navigate(goBackPath);
      return;
    }

    const cancelAndNavigate = async () => {
      try {
        await cancelTournament(id);
      } catch (err) {
        console.error('Failed to cancel tournament on timer expiry', err);
      } finally {
        navigate(goBackPath);
      }
    };

    void cancelAndNavigate();
  }, [isExpired, id, navigate, isJoinFlow, goBackPath]);

  const handleSubmit = async (address: string) => {
    setLoading(true);

    try {
      // GET-first: check if an assignment already exists for this (user, tournament)
      const existing = await getWalletAssignmentForTournament(id || '');

      if (existing) {
        // TB-RULE-DATA-NORMALIZE: handle both Strapi shapes
        const existingAddr = existing?.attributes?.address ?? existing?.address;
        if (existingAddr?.toLowerCase() !== address.toLowerCase()) {
          throw new Error(
            'Wallet address for this tournament already declared. Cannot change after declaration.',
          );
        }
      } else {
        await assignWalletToTournament(address, id || '', hash);
      }

      navigate(routes.PAYMENT_QR.replace(':id', id || '') + '?mode=wallet-assignment', {
        state: {
          from: isJoinFlow ? 'tournament-join' : 'tournament-create',
          tournamentId: id,
          hash,
        },
      });
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!id) {
      navigate(goBackPath);
      return;
    }

    if (isJoinFlow) {
      navigate(goBackPath);
      return;
    }

    setLoading(true);
    try {
      await cancelTournament(id);
    } catch (err) {
      console.error('Failed to cancel tournament from wallet entry', err);
    } finally {
      setLoading(false);
      navigate(goBackPath);
    }
  };

  return (
    <BaseWrapper compact>
      <GradientSection>
        <HeaderStack>
          <ProgressBar currentStep="payment" />
        </HeaderStack>

        <Belt>
          <NetworkChip chain={chain} />
          <TimerDisplay timeLeft={formatTime()} isWarning={timeLeft < 60} />
        </Belt>

        <FlowTag kind="from" />

        <Content>
          <WalletAddressBanner timeLeft={formatTime()} />
          <WalletAddressForm
            chain={chain}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
          />
        </Content>
      </GradientSection>
    </BaseWrapper>
  );
};

export default WalletAddressEntry;
